import { Input, Modal, message } from 'antd';
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { userConfigStore } from '@/stores';
import type { ConfigDefinitionItem } from '@/types';

const { TextArea } = Input;

interface EditDefinitionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  item: ConfigDefinitionItem | null;
  onClose: () => void;
}

function isValidJson(value: string): boolean {
  try {
    JSON.parse(value);
    return true;
  } catch {
    return false;
  }
}

export function EditDefinitionDialog({
  open,
  onOpenChange,
  item,
  onClose,
}: EditDefinitionDialogProps) {
  const [jsonValue, setJsonValue] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [jsonError, setJsonError] = useState('');

  useEffect(() => {
    if (item) {
      setJsonValue(item.jsonValue);
      setDescription(item.description);
      setJsonError('');
    }
  }, [item]);

  const handleClose = () => {
    onOpenChange(false);
    onClose();
  };

  const handleJsonChange = (value: string) => {
    setJsonValue(value);
    if (value && !isValidJson(value)) {
      setJsonError('请输入合法的 JSON 格式');
    } else {
      setJsonError('');
    }
  };

  const isSubmitDisabled = loading || !jsonValue.trim() || !!jsonError;

  const handleSubmit = async () => {
    if (!item || !jsonValue.trim()) return;
    if (!isValidJson(jsonValue)) {
      setJsonError('请输入合法的 JSON 格式');
      return;
    }

    setLoading(true);
    try {
      await userConfigStore.updateDefinition({
        config_key: item.configKey,
        json_value: jsonValue.trim(),
        description: description.trim(),
      });
      message.success('更新成功');
      handleClose();
    } catch (error) {
      console.error('更新失败:', error);
      message.error('更新失败');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      title={`编辑配置：${item?.configKey ?? ''}`}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={handleClose}>
            取消
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitDisabled}>
            {loading ? '保存中...' : '保存'}
          </Button>
        </div>
      }
      centered
      width={560}
      styles={{
        container: {
          padding: '24px',
          borderRadius: '8px',
          backgroundColor: 'var(--card)',
          border: '1px solid var(--border)',
        },
      }}
    >
      <div className="space-y-4 pt-2">
        <div className="space-y-2">
          <Label>配置键</Label>
          <Input value={item?.configKey ?? ''} disabled className="font-mono" />
        </div>

        <div className="space-y-2">
          <Label htmlFor="editDescription">描述</Label>
          <Input
            id="editDescription"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="配置用途说明"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="editJsonValue">JSON 值 *</Label>
          <TextArea
            id="editJsonValue"
            value={jsonValue}
            onChange={(e) => handleJsonChange(e.target.value)}
            rows={4}
            className="font-mono text-sm"
            status={jsonError ? 'error' : undefined}
          />
          {jsonError && <p className="text-xs text-red-500">{jsonError}</p>}
        </div>
      </div>
    </Modal>
  );
}
